import { Grid, Pagination } from '@mui/material'
import { useEffect, useMemo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryParams, withDefault, NumberParam, DecodedValueMap } from 'use-query-params'
import useGetEventPatternList from '../../../hooks/data/eventPatterns/useGetEventPatternList'
import { queryNames } from '../../../hooks/queries'
import EmptyListPlaceholder from '../../common/EmptyListPlaceholder'
import ListConnectionError from '../../common/ListConnectionError'
import Loader from '../../common/Loader'
import { useQueryClient } from '@tanstack/react-query'
import Tile from './Tile'
import { BaseListSearchParams } from '../../../types/common'
import { contentContainerId } from '../../../utils/const'

const eventPatternsPerPage = Number.parseInt(process.env.REACT_APP_EVENT_PATTERNS_PER_PAGE!)

function isSearchParamsValid(params: DecodedValueMap<BaseListSearchParams>): boolean {
  return Number.isInteger(params.page) // must be a number, not a NaN
    && params.page >= 1  // page cannot be lower than 1
}

function Container(props: { children: ReactNode | ReactNode[] }) {
  return (
    <Grid 
      container 
      flexDirection='column'
      width='100%'
      marginY='1.5rem'
      gap='1.5rem'
    >
      {props.children}
    </Grid>
  )
}

export default function MainSection() {
  const texts = useTranslation('eventPatternList').t
  const queryClient = useQueryClient()

  const [searchParams, setSearchParams] = useQueryParams<BaseListSearchParams>({ 
    page: withDefault(NumberParam, 1)
  })

  const eventPatternList = useGetEventPatternList({
    limit: eventPatternsPerPage,
    offset: (searchParams.page - 1) * eventPatternsPerPage,
    timezone: process.env.REACT_APP_DEFAULT_TZ!,
    enabled: isSearchParamsValid(searchParams),
    onSuccess: (data) => {
      if (searchParams.page > 1 && data.eventPatterns.length === 0) {
        setSearchParams({
          page: Math.ceil((data.totalCount ?? 0) / eventPatternsPerPage),
        }, 'replaceIn')
      }
    }
  })

  const maxPage = useMemo(() => {
    return Math.ceil((eventPatternList.data?.totalCount ?? 0) / eventPatternsPerPage)
  }, [eventPatternList.data?.totalCount])

  useEffect(() => {
    document.getElementById(contentContainerId)?.scrollTo(0, 0)
  }, [searchParams.page])

  useEffect(() => {      
    if (!isSearchParamsValid(searchParams)) {
      setSearchParams({
        page: 1
      }, 'replaceIn')
      return
    }
    
    if (searchParams.page === 1) {
      setSearchParams({
        page: 1
      }, 'replaceIn')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onPageChange = (_: React.ChangeEvent<any>, value: number) => {
    setSearchParams({ page: value }, 'push')
  }

  if (eventPatternList.isFetching) {
    return (
      <Container>
        <Grid marginTop='10rem' width='100%'>
          <Loader />
        </Grid>
      </Container>
    )
  }

  const isPageNotFound = searchParams.page > 1 && eventPatternList.data!.eventPatterns.length === 0
  if (!isSearchParamsValid(searchParams) || isPageNotFound) {
    return <></>
  }
  
  if (eventPatternList.isError) {
    return (
      <Container>
        <Grid marginTop='10rem' width='100%'>
          <ListConnectionError 
            onReload={() => {
              queryClient.resetQueries([
                queryNames.getEventPatternList,
                eventPatternsPerPage,
                (searchParams.page - 1) * eventPatternsPerPage
              ])
            }}
          />
        </Grid>
      </Container>
    )
  }

  if (eventPatternList.data!.eventPatterns.length === 0) {
    return (
      <Container>
        <Grid marginTop='10rem' width='100%'>
          <EmptyListPlaceholder
            title={texts('no_data_title')}
            description={texts('no_data_description')}
          />
        </Grid>
      </Container>
    )
  }

  return (
    <Container>
      <Grid
        item
        container
        width='100%'
        flexDirection='column'
        gap='.75rem'
      >
        {
          eventPatternList.data!.eventPatterns.map(eventPattern => 
            <Tile 
              key={eventPattern.uuid} 
              eventPattern={eventPattern} 
            />
          )
        }
      </Grid>
      {
        maxPage > 1
        && <Pagination 
            count={maxPage}
            page={searchParams.page}
            color='secondary'
            onChange={onPageChange}
            sx={{
              alignSelf: 'center'
            }}
          />
      }
    </Container>
  )
}